import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';

interface HeaderNotificationProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  type?: 'success' | 'info' | 'error';
  duration?: number;
}

export function HeaderNotification({
  isOpen,
  onClose,
  message,
  type = 'info',
  duration = 3000,
}: HeaderNotificationProps) {
  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose]);

  const bgColor =
    type === 'success'
      ? 'bg-green-500/20 border-green-500/40'
      : type === 'error'
        ? 'bg-red-500/20 border-red-500/40'
        : 'bg-blue-500/20 border-blue-500/40';

  const textColor =
    type === 'success'
      ? 'text-green-400'
      : type === 'error'
        ? 'text-red-400'
        : 'text-blue-400';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 pointer-events-none"
        >
          <div
            className={`${bgColor} ${textColor} border rounded-lg px-6 py-3 shadow-lg backdrop-blur-sm pointer-events-auto`}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{message}</span>
              <button
                onClick={onClose}
                className="ml-2 text-white/50 hover:text-white/80 transition-colors"
              >
                ×
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
